import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, Plus, Trash2, Eye, Edit, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, authService } from '../utils/auth';
import { toast } from 'sonner';

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const isAdmin = authService.isAdmin();

  const requirementOptions = ['Forging', 'Machining', 'Welding', 'Silver Plating', 'Assembly', 'Heat Treatment'];

  useEffect(() => {
    fetchCustomers();
  }, [page]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const data = await apiRequest(`/api/customers?skip=${skip}&limit=${pageSize}`);
      setCustomers(data.customers || []);
      setTotalCount(data.total || data.customers?.length || 0);
    } catch (error) {
      toast.error('Failed to fetch customers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customerCode) => {
    if (!window.confirm(`Delete customer ${customerCode}?`)) return;

    try {
      await apiRequest(`/api/customers/${customerCode}`, { method: 'DELETE' });
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error(error.message || 'Failed to delete customer');
    }
  };

  const openEditModal = (customer) => {
    setEditCustomer(customer);
    setEditFormData({
      customer_code: customer.customer_code || '',
      company_name: customer.company_name || '',
      rfq_id_prefix: customer.rfq_id_prefix || '',
      city: customer.city || '',
      state: customer.state || '',
      country: customer.country || '',
      business_type: customer.business_type || 'auto',
      requirements: customer.requirements || [],
      sale_type: customer.sale_type || 'domestic',
      contact_person: customer.contact_person || '',
      mobile: customer.mobile || '',
      website: customer.website || '',
      email: customer.email || '',
    });
  };

  const handleEditSave = async () => {
    if (!editCustomer) return;
    setSaving(true);
    try {
      await apiRequest(`/api/customers/${editCustomer.customer_code}`, {
        method: 'PUT',
        body: JSON.stringify(editFormData),
      });
      toast.success('Customer updated successfully');
      setEditCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleRequirementToggle = (req) => {
    setEditFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.includes(req)
        ? prev.requirements.filter((r) => r !== req)
        : [...prev.requirements, req],
    }));
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customer_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div data-testid="customers-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-[#0A2540]">Customers</h2>
            <p className="text-sm text-gray-600 mt-1">Manage your customer database</p>
          </div>
          <Button
            data-testid="add-customer-button"
            onClick={() => navigate('/customer-registration')}
            className="bg-[#0A2540] hover:bg-[#0d2f52] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>

        <Card className="p-6 border-gray-200">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-input"
                placeholder="Search by company name or customer code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading customers...</p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No customers found</p>
              <Button
                data-testid="empty-state-add-button"
                onClick={() => navigate('/customer-registration')}
                variant="outline"
                className="mt-4"
              >
                Register First Customer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer Code</TableHead>
                    <TableHead>Company Name</TableHead>
                    <TableHead>RFQ Prefix</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Business Type</TableHead>
                    <TableHead>Business Value</TableHead>
                    <TableHead>Contact Person</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer, index) => (
                    <TableRow key={index} data-testid={`customer-row-${index}`}>
                      <TableCell className="font-medium">{customer.customer_code}</TableCell>
                      <TableCell>{customer.company_name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {customer.rfq_id_prefix || 'RFQ-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {customer.city}, {customer.state}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {customer.business_type === 'auto' ? 'Automotive' : 'Non-Automotive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-green-600">
                          ₹{(customer.business_value || 0).toLocaleString('en-IN')}
                        </span>
                      </TableCell>
                      <TableCell>{customer.contact_person}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewCustomer(customer)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(customer)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(customer.customer_code)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination - always show if there are customers */}
          {filteredCustomers.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <p className="text-sm text-gray-600">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} customers
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">Page {page}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * pageSize >= totalCount}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* View Customer Modal */}
      {viewCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#0A2540]">Customer Details</h2>
                <button
                  onClick={() => setViewCustomer(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Customer Code</Label>
                    <p className="font-semibold text-lg">{viewCustomer.customer_code}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">RFQ Prefix</Label>
                    <p className="font-semibold text-lg">{viewCustomer.rfq_id_prefix}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-600">Company Name</Label>
                  <p className="font-semibold text-lg">{viewCustomer.company_name}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-600">City</Label>
                    <p className="font-medium">{viewCustomer.city}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">State</Label>
                    <p className="font-medium">{viewCustomer.state}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Country</Label>
                    <p className="font-medium">{viewCustomer.country}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Business Type</Label>
                    <p className="font-medium">{viewCustomer.business_type === 'auto' ? 'Automotive' : 'Non-Automotive'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Sale Type</Label>
                    <p className="font-medium">{viewCustomer.sale_type === 'domestic' ? 'Domestic' : 'Export'}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-600">Requirements</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {viewCustomer.requirements?.map((req, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {req}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Contact Person</Label>
                    <p className="font-medium">{viewCustomer.contact_person}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Mobile</Label>
                    <p className="font-medium">{viewCustomer.mobile}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-medium">{viewCustomer.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Website</Label>
                    <p className="font-medium">{viewCustomer.website || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setViewCustomer(null)} className="bg-[#0A2540]">
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Customer Modal */}
      {editCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#0A2540]">Edit Customer</h2>
                <button
                  onClick={() => setEditCustomer(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Customer Code</Label>
                    <Input 
                      value={editFormData.customer_code || editCustomer.customer_code} 
                      onChange={(e) => setEditFormData({ ...editFormData, customer_code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">RFQ ID Prefix *</Label>
                    <Input
                      value={editFormData.rfq_id_prefix}
                      onChange={(e) => setEditFormData({ ...editFormData, rfq_id_prefix: e.target.value.toUpperCase() })}
                      placeholder="e.g., TATA-"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-600">Company Name *</Label>
                  <Input
                    value={editFormData.company_name}
                    onChange={(e) => setEditFormData({ ...editFormData, company_name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-600">City *</Label>
                    <Input
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">State *</Label>
                    <Input
                      value={editFormData.state}
                      onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Country *</Label>
                    <Input
                      value={editFormData.country}
                      onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Business Type *</Label>
                    <select
                      value={editFormData.business_type}
                      onChange={(e) => setEditFormData({ ...editFormData, business_type: e.target.value })}
                      className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="auto">Automotive</option>
                      <option value="non-auto">Non-Automotive</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-600">Sale Type *</Label>
                    <select
                      value={editFormData.sale_type}
                      onChange={(e) => setEditFormData({ ...editFormData, sale_type: e.target.value })}
                      className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="domestic">Domestic</option>
                      <option value="export">Export</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-600">Requirements</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {requirementOptions.map((req) => (
                      <label key={req} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editFormData.requirements?.includes(req)}
                          onChange={() => handleRequirementToggle(req)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{req}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Contact Person *</Label>
                    <Input
                      value={editFormData.contact_person}
                      onChange={(e) => setEditFormData({ ...editFormData, contact_person: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Mobile *</Label>
                    <Input
                      value={editFormData.mobile}
                      onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Email *</Label>
                    <Input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Website</Label>
                    <Input
                      value={editFormData.website}
                      onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setEditCustomer(null)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSave} className="bg-[#0A2540]" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}